/**
 * Reset Password Service
 *
 * Calls the reset-user-password Edge Function to set a new password for a
 * user within the caller's admin scope (platform → any user; tenant → same-tenant;
 * workspace → same-workspace).
 */

import { getSupabaseClient } from './supabaseClient';

export interface ResetPasswordInput {
  targetUserId: string;
  newPassword: string;
}

export async function resetUserPassword(input: ResetPasswordInput): Promise<void> {
  const supabase = getSupabaseClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('Not signed in — sign in again and retry.');
  }

  const { data, error } = await supabase.functions.invoke('reset-user-password', {
    body: {
      targetUserId: input.targetUserId,
      newPassword: input.newPassword,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (error) {
    const ctx = (error as Record<string, unknown>).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const body = await (ctx as Response).json() as { error?: string };
        if (body?.error) throw new Error(body.error);
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== 'Failed to parse') {
          throw parseErr;
        }
      }
    }
    const msg = error.message || 'Failed to reset password';
    const isNotFound = msg.includes('404') || msg.includes('Failed to fetch') || msg.includes('Load failed');
    const hint = isNotFound ? ' Deploy the function: supabase functions deploy reset-user-password' : '';
    throw new Error(msg + hint);
  }

  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
}
